import { costLagForAccounts } from '../cost/source.js';
export class InvalidBudgetError extends Error {
}
/** The currency a budget is set and summed in. */
export const BUDGET_CURRENCY = 'USD';
/**
 * Why an account's figure is left out of a sum of accounts, or `undefined`
 * when it may be added (IM-215).
 *
 * Two reasons, and both are refusals rather than adjustments:
 *
 * - **A currency other than the budget's.** Azure bills a subscription in its
 *   billing account's currency, which need not be dollars, and Cost
 *   Management reports in it. Converting would add a second number with its
 *   own source, date and lag to a figure that decides whether new instances
 *   are refused, so the account is named instead, with this sentence. Every
 *   account Qrvey pays for today bills in dollars, so this costs nothing now
 *   and says so on the day it would start to matter. One account's own screen
 *   shows its figure in its own currency; only sums refuse.
 * - **A figure that covers less than the account** (`CostCoverage`): an Azure
 *   subscription delegated one resource group at a time. Its figure is real
 *   and is shown on its own screen, but summed it would read as the account's
 *   whole cost, and the part it misses is the node pools.
 *
 * Used by the budget evaluation and by the fleet's cost routes, so the two
 * cannot disagree about which accounts are in a total.
 */
export const whyNotSummed = (cost) => {
    if (cost.currency !== BUDGET_CURRENCY) {
        return `This account bills in ${cost.currency}, and Qrvey's totals are in ${BUDGET_CURRENCY}. It is left out of the total rather than converted, because a converted figure would rest on an exchange rate nobody here chose.`;
    }
    if (cost.coverage !== undefined) {
        return `Only part of this account could be read, so it is left out of the total: ${cost.coverage.note}`;
    }
    return undefined;
};
/** The lag sentence for the accounts an evaluation names, read off their keys. */
const lagOf = (evaluation) => costLagForAccounts([...evaluation.accounts, ...evaluation.unread].map((a) => a.accountKey));
/**
 * What a budget request asks for, refused with a sentence when it is wrong.
 *
 * Shared so the console refuses before the request with the same wording — the
 * arrangement every other form here uses.
 */
export const validateBudget = (input) => {
    if (typeof input !== 'object' || input === null || Array.isArray(input)) {
        throw new InvalidBudgetError('the body must be a JSON object');
    }
    const { monthlyAmount, warnAtPercent, blocksNewInstances } = input;
    if (typeof monthlyAmount !== 'number' || !Number.isFinite(monthlyAmount) || monthlyAmount <= 0) {
        throw new InvalidBudgetError('monthlyAmount must be a positive number of US dollars');
    }
    if (typeof warnAtPercent !== 'number' ||
        !Number.isInteger(warnAtPercent) ||
        warnAtPercent < 1 ||
        warnAtPercent > 99) {
        throw new InvalidBudgetError('warnAtPercent must be a whole number from 1 to 99');
    }
    if (typeof blocksNewInstances !== 'boolean') {
        throw new InvalidBudgetError('blocksNewInstances must be true or false');
    }
    return { monthlyAmount, warnAtPercent, blocksNewInstances };
};
/** `YYYY-MM` in UTC. */
export const budgetMonth = (at) => at.toISOString().slice(0, 7);
/**
 * Folds today's readings into the month.
 *
 * A crossing is recorded once, on the evaluation that first sees it, and
 * carried forward for the rest of the month — so the alert is the day it
 * happened, not every morning after, and a month that crosses and then drops
 * back (a credit, a correction) still says it crossed.
 *
 * `previous` from another month is ignored: the month turning is a fresh start,
 * which is also what lifts a block without anybody doing anything.
 */
export const evaluateBudget = (budget, previous, readings, unread, period, now) => {
    const month = budgetMonth(now);
    const carried = previous?.month === month ? previous : undefined;
    const total = readings.reduce((sum, reading) => sum + reading.amount, 0);
    const at = now.toISOString();
    const warnLine = (budget.monthlyAmount * budget.warnAtPercent) / 100;
    const crossed = [];
    let warnCrossedAt = carried?.warnCrossedAt;
    let capCrossedAt = carried?.capCrossedAt;
    if (warnCrossedAt === undefined && total >= warnLine) {
        warnCrossedAt = at;
        crossed.push('warn');
    }
    if (capCrossedAt === undefined && total >= budget.monthlyAmount) {
        capCrossedAt = at;
        crossed.push('cap');
    }
    return {
        evaluation: {
            month,
            period,
            evaluatedAt: at,
            total,
            currency: 'USD',
            accounts: readings,
            unread,
            ...(warnCrossedAt === undefined ? {} : { warnCrossedAt }),
            ...(capCrossedAt === undefined ? {} : { capCrossedAt }),
        },
        crossed,
    };
};
/**
 * Whether new instances are refused right now.
 *
 * Judged against the **current** cap and this month's evaluation, not against
 * the crossing mark alone — so raising the cap or turning blocking off lifts
 * the block at once, without waiting for tomorrow's evaluation. That is what
 * makes lifting it one action.
 */
export const budgetBlocksNewInstances = (budget, evaluation, now) => budget !== undefined &&
    budget.blocksNewInstances &&
    evaluation !== undefined &&
    evaluation.month === budgetMonth(now) &&
    evaluation.total >= budget.monthlyAmount;
const money = (amount) => `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
/** What a refused create says, naming the cap and what lifts it. */
export const describeBudgetBlock = (budget, evaluation) => `Qrvey's own accounts have spent ${money(evaluation.total)} this month (to ${evaluation.period.to}, as of ${evaluation.evaluatedAt}), over the ${money(budget.monthlyAmount)} platform budget ${budget.by} set on ${budget.at}. New instances are refused until the budget is raised, blocking is turned off, or the month ends. Existing instances, destroys and plans are unaffected.`;
/** The alert for a line crossed today. */
export const formatBudgetAlert = (budget, evaluation, crossed, consoleUrl) => {
    const cap = crossed.includes('cap');
    const lag = lagOf(evaluation);
    const subject = cap
        ? `Qrvey's own accounts are over this month's ${money(budget.monthlyAmount)} budget`
        : `Qrvey's own accounts have passed ${String(budget.warnAtPercent)}% of this month's budget`;
    const lines = [
        `Spent this month, as far as could be read: ${money(evaluation.total)} of ${money(budget.monthlyAmount)} (${evaluation.period.from} to ${evaluation.period.to}).${lag === '' ? '' : ` ${lag}`}`,
        cap
            ? budget.blocksNewInstances
                ? 'New instances in Qrvey-owned accounts are now refused. Existing instances, destroys and plans are unaffected. Raise the budget or turn blocking off to lift it.'
                : 'Blocking is off, so nothing is refused — this is a report only.'
            : `The cap itself is ${money(budget.monthlyAmount)}.`,
        ...(evaluation.unread.length === 0
            ? []
            : [
                `${String(evaluation.unread.length)} account${evaluation.unread.length === 1 ? '' : 's'} could not be read and ${evaluation.unread.length === 1 ? 'is' : 'are'} not in the total: ${evaluation.unread.map((g) => g.accountKey).join(', ')}.`,
            ]),
        // The fleet's Cost tab, where the chart and the budget are — a tab is a
        // path, so the alert can land on the answer rather than on the list.
        `${consoleUrl}/fleet/cost`,
    ];
    return { subject, body: lines.join('\n\n') };
};
//# sourceMappingURL=budget.js.map